import { DOCUMENT } from '@angular/common';
import { inject, Injectable, Renderer2, RendererFactory2 } from '@angular/core';
import { RECAPTCHA_SITE_KEY } from '@webapp/core/config';
import { CaptchaPort } from './captcha.port';

declare const grecaptcha: {
  enterprise: {
    ready: (callback: () => void) => void;
    execute: (siteKey: string, options: { action: string }) => Promise<string>;
  };
};

// Moved from libs/core/src/lib/services/recaptcha.service.ts (design D1
// extension). Web-only reCAPTCHA execution — see captcha.port.ts for why the
// native counterpart is a no-op instead of trying to make this work in a
// Capacitor WebView.
@Injectable({ providedIn: 'root' })
export class WebCaptchaService implements CaptchaPort {
  private readonly document = inject(DOCUMENT);
  private readonly siteKey = inject(RECAPTCHA_SITE_KEY);
  private readonly renderer: Renderer2 = inject(RendererFactory2).createRenderer(null, null);
  private scriptLoadPromise: Promise<void> | null = null;

  async execute(action: string): Promise<string> {
    await this.loadScript();

    return new Promise((resolve, reject) => {
      grecaptcha.enterprise.ready(() => {
        grecaptcha.enterprise.execute(this.siteKey, { action }).then(resolve, reject);
      });
    });
  }

  private loadScript(): Promise<void> {
    if (this.scriptLoadPromise) return this.scriptLoadPromise;

    this.scriptLoadPromise = new Promise((resolve, reject) => {
      const script = this.renderer.createElement('script');
      this.renderer.setAttribute(
        script,
        'src',
        `https://www.google.com/recaptcha/enterprise.js?render=${this.siteKey}`
      );
      this.renderer.listen(script, 'load', () => resolve());
      this.renderer.listen(script, 'error', () => reject(new Error('Failed to load reCAPTCHA script')));
      this.renderer.appendChild(this.document.head, script);
    });

    return this.scriptLoadPromise;
  }
}
